import React from 'react';
import { StepContainer } from '../StepContainer';
import { FormSection } from './form/formSection';
import { FormItem } from './form/formItem';

interface ExterieursStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const ExterieursStep: React.FC<ExterieursStepProps> = ({
  data = {
    facades: {},
    toituresExterieures: {},
    amenagements: {},
    clotures: {},
    portails: {},
    espacesVerts: {},
    eclairageExterieur: {},
    piscine: {}
  },
  onChange,
  onNext,
  onBack
}) => {
  const updateSection = (section: string, key: string, value: any) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [key]: value
      }
    });
  };

  return (
    <StepContainer
      title="Extérieurs et espaces verts"
      description="Détaillez les travaux d'aménagement extérieur"
      onNext={onNext}
      onBack={onBack}
    >
      <div className="space-y-6">
        <FormSection
          title="1. Travaux sur les façades"
          description="Travaux de rénovation des façades"
        >
          <div className="space-y-4">
            <FormItem
              label="Nettoyage de façade"
              value={data.facades?.nettoyage || { selected: false }}
              onChange={(value) => updateSection('facades', 'nettoyage', value)}
              withSurface
            />
            <FormItem
              label="Ravalement de façade"
              value={data.facades?.ravalement || { selected: false }}
              onChange={(value) => updateSection('facades', 'ravalement', value)}
              withSurface
            />
            <FormItem
              label="Réparation de fissures"
              value={data.facades?.reparationFissures || { selected: false }}
              onChange={(value) => updateSection('facades', 'reparationFissures', value)}
              withLength
            />
          </div>
        </FormSection>

        <FormSection
          title="2. Toitures extérieures"
          description="Aménagement des toitures-terrasses"
        >
          <div className="space-y-4">
            <FormItem
              label="Création de toiture-terrasse"
              value={data.toituresExterieures?.terrasse || { selected: false }}
              onChange={(value) => updateSection('toituresExterieures', 'terrasse', value)}
              withSurface
            />
            <FormItem
              label="Installation de pergola"
              value={data.toituresExterieures?.pergola || { selected: false }}
              onChange={(value) => updateSection('toituresExterieures', 'pergola', value)}
              withSurface
            />
          </div>
        </FormSection>

        <FormSection
          title="3. Aménagements extérieurs"
          description="Terrasses et allées"
        >
          <div className="space-y-4">
            <FormItem
              label="Terrasse en carrelage"
              value={data.amenagements?.terrasseCarrelage || { selected: false }}
              onChange={(value) => updateSection('amenagements', 'terrasseCarrelage', value)}
              withSurface
            />
            <FormItem
              label="Terrasse en béton imprimé"
              value={data.amenagements?.terrasseBeton || { selected: false }}
              onChange={(value) => updateSection('amenagements', 'terrasseBeton', value)}
              withSurface
            />
            <FormItem
              label="Terrasse en bois"
              value={data.amenagements?.terrasseBois || { selected: false }}
              onChange={(value) => updateSection('amenagements', 'terrasseBois', value)}
              withSurface
            />
            <FormItem
              label="Allée en pavés"
              value={data.amenagements?.alleePaves || { selected: false }}
              onChange={(value) => updateSection('amenagements', 'alleePaves', value)}
              withLength
            />
          </div>
        </FormSection>

        <FormSection
          title="4. Clôtures et portails"
          description="Installation ou remplacement"
        >
          <div className="space-y-4">
            <FormItem
              label="Clôture en bois"
              value={data.clotures?.bois || { selected: false }}
              onChange={(value) => updateSection('clotures', 'bois', value)}
              withLength
            />
            <FormItem
              label="Clôture en métal"
              value={data.clotures?.metal || { selected: false }}
              onChange={(value) => updateSection('clotures', 'metal', value)}
              withLength
            />
            <FormItem
              label="Clôture en PVC"
              value={data.clotures?.pvc || { selected: false }}
              onChange={(value) => updateSection('clotures', 'pvc', value)}
              withLength
            />
            <FormItem
              label="Portail battant motorisé"
              value={data.portails?.battant || { selected: false }}
              onChange={(value) => updateSection('portails', 'battant', value)}
              withQuantity
            />
            <FormItem
              label="Portail coulissant motorisé"
              value={data.portails?.coulissant || { selected: false }}
              onChange={(value) => updateSection('portails', 'coulissant', value)}
              withQuantity
            />
          </div>
        </FormSection>

        <FormSection
          title="5. Espaces verts"
          description="Aménagement paysager"
        >
          <div className="space-y-4">
            <FormItem
              label="Création de pelouse"
              value={data.espacesVerts?.pelouse || { selected: false }}
              onChange={(value) => updateSection('espacesVerts', 'pelouse', value)}
              withSurface
            />
            <FormItem
              label="Plantation d'arbustes et vivaces"
              value={data.espacesVerts?.plantations || { selected: false }}
              onChange={(value) => updateSection('espacesVerts', 'plantations', value)}
              withSurface
            />
            <FormItem
              label="Création de massifs fleuris"
              value={data.espacesVerts?.massifs || { selected: false }}
              onChange={(value) => updateSection('espacesVerts', 'massifs', value)}
              withSurface
            />
            <FormItem
              label="Installation système d'arrosage automatique"
              value={data.espacesVerts?.arrosageAuto || { selected: false }}
              onChange={(value) => updateSection('espacesVerts', 'arrosageAuto', value)}
              withSurface
            />
          </div>
        </FormSection>

        <FormSection
          title="6. Éclairage extérieur"
          description="Installation d'éclairages"
        >
          <div className="space-y-4">
            <FormItem
              label="Spots encastrés"
              value={data.eclairageExterieur?.spots || { selected: false }}
              onChange={(value) => updateSection('eclairageExterieur', 'spots', value)}
              withQuantity
            />
            <FormItem
              label="Lampadaires"
              value={data.eclairageExterieur?.lampadaires || { selected: false }}
              onChange={(value) => updateSection('eclairageExterieur', 'lampadaires', value)}
              withQuantity
            />
            <FormItem
              label="Appliques murales"
              value={data.eclairageExterieur?.appliques || { selected: false }}
              onChange={(value) => updateSection('eclairageExterieur', 'appliques', value)}
              withQuantity
            />
          </div>
        </FormSection>

        <FormSection
          title="7. Piscine"
          description="Installation de piscine"
        >
          <div className="space-y-4">
            <FormItem
              label="Piscine enterrée"
              value={data.piscine?.enterree || { selected: false }}
              onChange={(value) => updateSection('piscine', 'enterree', value)}
              withSurface
            />
            <FormItem
              label="Piscine hors-sol"
              value={data.piscine?.horsSol || { selected: false }}
              onChange={(value) => updateSection('piscine', 'horsSol', value)}
              withSurface
            />
          </div>
        </FormSection>
      </div>
    </StepContainer>
  );
};

export default ExterieursStep;